// Whether the booking link actually reached the patient.
//
// A live link holds times in the diary. One that was made but never sent holds
// them for nobody — the worst outcome the holds feature can produce, and
// invisible until now: the block said "a link is out for this patient" whether
// or not a word had left the practice.
//
// Deliberately unlike confirmationSent.ts, which refuses to match on text
// because staff reword confirmations constantly and fold them into longer
// replies. A link is different: it either is in the message or it is not, and
// the patient cannot book without it. So matching on the link's path is the
// honest test here, and the copy says "sent from here" — a link pasted into
// Spruce by hand is not something this can see.

export interface ThreadMessage {
  direction?: string;
  createdAt?: string | Date | null;
  isInternalNote?: boolean;
  content?: string | null;
  message?: string | null;
  text?: string | null;
}

const at = (value: string | Date | null | undefined): number => {
  if (!value) return NaN;
  const ms = value instanceof Date ? value.getTime() : Date.parse(String(value));
  return Number.isNaN(ms) ? NaN : ms;
};

const bodyOf = (m: ThreadMessage): string =>
  String(m?.content ?? m?.message ?? m?.text ?? '');

// A booking link in a message: /b/<token> as links are minted now, or
// /book/<token> as they were. A token is at least eight url-safe characters,
// so "/b/" on its own, or a path that merely starts with b, is not one.
const BOOKING_LINK = /\/(?:b|book)\/[A-Za-z0-9_-]{8,}/;

export function linkSentAt(
  createdAt: string | Date | null | undefined,
  messages: ThreadMessage[] | null | undefined,
): Date | null {
  const made = at(createdAt);
  if (Number.isNaN(made)) return null;

  const candidates = (messages || [])
    // Only what actually goes to the patient. An internal note is a note to
    // ourselves, and the booking-link bookkeeping entry is direction 'none'
    // because nobody sent anything.
    .filter((m) => m?.direction === 'outbound' && !m?.isInternalNote)
    .filter((m) => BOOKING_LINK.test(bodyOf(m)))
    .map((m) => at(m?.createdAt))
    .filter((ms) => !Number.isNaN(ms) && ms >= made)
    .sort((a, b) => a - b);

  return candidates.length ? new Date(candidates[0]) : null;
}

export function linkSent(
  createdAt: string | Date | null | undefined,
  messages: ThreadMessage[] | null | undefined,
): boolean {
  return linkSentAt(createdAt, messages) !== null;
}
